import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ChatSettings,
  ChatSettingsCreatedEnvelope,
  ChatSettingsDeletedEnvelope,
  ChatSettingsQuery,
  ChatSettingsQueryBuilder,
  ChatSettingsUpdatedEnvelope,
  QueryChatSettingsResponse,
  UpdateChatSettings,
  createChatSettings as universalCreateChatSettings,
  deleteChatSettings as universalDeleteChatSettings,
  getChatSettings as universalGetChatSettings,
  queryChatSettings as universalQueryChatSettings,
  typedQueryChatSettings as universalTypedQueryChatSettings,
  updateChatSettings as universalUpdateChatSettings,
} from './forms-ai-v1-chat-settings-chat-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/forms' };

export function createChatSettings(
  httpClient: HttpClient
): CreateChatSettingsSignature {
  return (chatSettings: NonNullablePaths<ChatSettings, `_id`, 2>) =>
    universalCreateChatSettings(
      chatSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateChatSettingsSignature {
  /**
   * Creates a chat settings entity.
   *
   * The settings specified in a chat settings entity apply to the form with the same ID as the chat settings entity. Each form can have exactly one set of chat settings.
   * @param - Chat settings to be created.
   * @returns Created chat settings.
   */
  (chatSettings: NonNullablePaths<ChatSettings, `_id`, 2>): Promise<
    NonNullablePaths<ChatSettings, `_id` | `personality`, 2>
  >;
}

export function updateChatSettings(
  httpClient: HttpClient
): UpdateChatSettingsSignature {
  return (_id: string, chatSettings: UpdateChatSettings) =>
    universalUpdateChatSettings(
      _id,
      chatSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateChatSettingsSignature {
  /**
   * Updates a chat settings entity.
   *
   * Changes take effect immediately for new interactive form sessions.
   * @param - The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.
   * @returns Updated chat settings.
   */
  (_id: string, chatSettings: UpdateChatSettings): Promise<
    NonNullablePaths<ChatSettings, `_id` | `personality`, 2>
  >;
}

export function deleteChatSettings(
  httpClient: HttpClient
): DeleteChatSettingsSignature {
  return (chatSettingsId: string) =>
    universalDeleteChatSettings(
      chatSettingsId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteChatSettingsSignature {
  /**
   * Deletes a chat settings entity.
   * @param - Chat settings ID.
   */
  (chatSettingsId: string): Promise<void>;
}

export function getChatSettings(
  httpClient: HttpClient
): GetChatSettingsSignature {
  return (chatSettingsId: string) =>
    universalGetChatSettings(
      chatSettingsId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetChatSettingsSignature {
  /**
   * Retrieves a chat settings entity.
   * @param - Chat settings ID.
   * @returns Retrieved chat settings.
   */
  (chatSettingsId: string): Promise<
    NonNullablePaths<ChatSettings, `_id` | `personality`, 2>
  >;
}

export function queryChatSettings(
  httpClient: HttpClient
): QueryChatSettingsSignature {
  return () =>
    universalQueryChatSettings(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryChatSettingsSignature {
  /**
   * Retrieves a list of chat settings entities given the provided paging, filtering, and sorting.
   *
   * ### Defaults
   * Query Chat Settings has the following default settings, which you can override:
   * + Sorted by `id` in ascending order.
   * + `cursorPaging.limit` set to `100`.
   */
  (): ChatSettingsQueryBuilder;
}

export function typedQueryChatSettings(
  httpClient: HttpClient
): TypedQueryChatSettingsSignature {
  return (query: ChatSettingsQuery) =>
    universalTypedQueryChatSettings(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryChatSettingsSignature {
  /** */
  (query: ChatSettingsQuery): Promise<
    NonNullablePaths<
      QueryChatSettingsResponse,
      | `chatSettings`
      | `chatSettings.${number}._id`
      | `chatSettings.${number}.personality`,
      4
    >
  >;
}

export const onChatSettingsCreated = EventDefinition(
  'wix.forms.ai.v1.chat_settings_created',
  true,
  (event: ChatSettingsCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ChatSettingsCreatedEnvelope>();
export const onChatSettingsDeleted = EventDefinition(
  'wix.forms.ai.v1.chat_settings_deleted',
  true,
  (event: ChatSettingsDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ChatSettingsDeletedEnvelope>();
export const onChatSettingsUpdated = EventDefinition(
  'wix.forms.ai.v1.chat_settings_updated',
  true,
  (event: ChatSettingsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<ChatSettingsUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  ChatSettings,
  ChatSettingsCreatedEnvelope,
  ChatSettingsDeletedEnvelope,
  ChatSettingsQueryBuilder,
  ChatSettingsQueryResult,
  ChatSettingsQuerySpec,
  ChatSettingsUpdatedEnvelope,
  CreateChatSettingsRequest,
  CreateChatSettingsResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteChatSettingsRequest,
  DeleteChatSettingsResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetChatSettingsRequest,
  GetChatSettingsResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  Personality,
  QueryChatSettingsRequest,
  QueryChatSettingsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UpdateChatSettings,
  UpdateChatSettingsRequest,
  UpdateChatSettingsResponse,
  WebhookIdentityType,
  utils,
} from './forms-ai-v1-chat-settings-chat-settings.universal.js';
